import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  Comment2Json,
  CommentCollectionJson,
  CommentJson,
  CommentJsonAddEdit,
  FilterComment,
  HistoryJson,
  RateJson,
} from "../types/comment.type";
import { baseQuery } from "common/api/BaseQuery";
import { RootState } from "app/store";
import Setting from "setting";
import { Jwt } from "common/types/Api";
import { applyMiddleware } from "@reduxjs/toolkit";
import { FilterImage, ImageCollectionJson } from "../types/gallery.type";
import BaseApi from "common/api/BaseApi";

type Error = {
  status:
    | number
    | "FETCH_ERROR"
    | "PARSING_ERROR"
    | "TIMEOUT_ERROR"
    | "CUSTOM_ERROR";
  errors: string[];
};
export const commentApi = createApi({
  reducerPath: "commentApi",
  baseQuery: baseQuery(),
  tagTypes: [
    `CommentPublic`,
    `CommentLogin`,
    `ReviewPublic`,
    `ReviewLogin`,
    `QaPublic`,
    `QaLogin`,
    `Rate`,
    `ImagePublic`,
    `LikedLogin`,
  ],
  endpoints: (builder) => ({
    // Api Public

    //getList
    getCommentListPublic: builder.query<CommentCollectionJson, FilterComment>({
      query: (params: FilterComment & Jwt) => ({
        url: "comments/public/comment",
        params: {
          ...params,
          customer_id:
            params.customer_id && params.customer_id > 0
              ? params.customer_id
              : undefined,
          item_id:
            params.item_id && params.item_id !== ""
              ? params.item_id
              : undefined,
          item_type:
            params.item_type && params.item_type !== ""
              ? params.item_type
              : undefined,
          parent_id:
            params.parent_id && params.parent_id > 0
              ? params.parent_id
              : undefined,
        },
        method: "get",
        timeout: 7000,
      }),

      transformErrorResponse(response, meta, arg) {
        return response;
      },
      transformResponse(response: CommentCollectionJson, _) {
        return response;
      },
      keepUnusedDataFor: 5,

      providesTags(result) {
        if (result) {
          const final = [
            ...result.items.map(({ id }: { id: number }) => ({
              type: "CommentPublic" as const,
              id: JSON.stringify(id),
            })),
            { type: "CommentPublic" as const, id: "COMMENT_LIST_PUBLIC" },
          ];
          return final;
        }

        return [{ type: "CommentPublic" as const, id: "COMMENT_LIST_PUBLIC" }];
      },
    }),

    //get Image list by product with pagination
    getImageListPublic: builder.query<ImageCollectionJson, FilterImage>({
      query: (params: FilterImage) => ({
        url: "comments/public/images",
        params: {
          ...params,
          item_id:
            params.item_id && params.item_id !== ""
              ? params.item_id
              : undefined,
          item_type:
            params.item_type && params.item_type !== ""
              ? params.item_type
              : undefined,
        },
        method: "get",
        timeout: 7000,
      }),
      providesTags(result) {
        if (result && result.items.length > 0) {
          const final = [
            ...result.items.map(({ id }: { id: number }) => ({
              type: "ImagePublic" as const,
              id: JSON.stringify(id),
            })),
            { type: "ImagePublic" as const, id: "IMAGE_LIST_PUBLIC" },
          ];
          return final;
        }

        return [{ type: "ImagePublic" as const, id: "IMAGE_LIST_PUBLIC" }];
      },
    }),

    //get list QA public
    getQAListPublic: builder.query<CommentCollectionJson, FilterComment>({
      query: (params: FilterComment) => ({
        url: "comments/public/qa",
        params: {
          ...params,
          item_id:
            params.item_id && params.item_id !== ""
              ? params.item_id
              : undefined,
          item_type:
            params.item_type && params.item_type !== ""
              ? params.item_type
              : undefined,
          parent_id:
            params.parent_id && params.parent_id > 0
              ? params.parent_id
              : undefined,
        },
        method: "get",
        timeout: 7000,
      }),
      keepUnusedDataFor: 0,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "QaPublic" as const,
                id,
              })),
              { type: "QaPublic", id: "QA_LIST_PUBLIC" },
            ]
          : [{ type: "QaPublic", id: "QA_LIST_PUBLIC" }],
    }),

    //get list review public
    getReviewListPublic: builder.query<CommentCollectionJson, FilterComment>({
      query: (params: FilterComment) => ({
        url: "comments/public/review",
        params: {
          ...params,
          item_id:
            params.item_id && params.item_id !== ""
              ? params.item_id
              : undefined,
          item_type:
            params.item_type && params.item_type !== ""
              ? params.item_type
              : undefined,
          parent_id:
            params.parent_id && params.parent_id > 0
              ? params.parent_id
              : undefined,
        },
        method: "get",
        timeout: 7000,
      }),
      keepUnusedDataFor: 0,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "ReviewPublic" as const,
                id,
              })),
              { type: "ReviewPublic", id: "REVIEW_LIST_PUBLIC" },
            ]
          : [{ type: "ReviewPublic", id: "REVIEW_LIST_PUBLIC" }],
    }),

    //get Leave test
    getCommentListTest: builder.query<CommentCollectionJson, void>({
      query: (body) => ({
        url: "leaves/employee",
        params: {
          __jwtAuthorization: localStorage.getItem("MAIN:token")
            ? localStorage.getItem("MAIN:token")
            : undefined,
        },
        method: "get",
      }),
    }),

    //get Rate

    getRatePublic: builder.query<
      RateJson,
      Pick<CommentJson, "item_type" | "item_id">
    >({
      query: (params) => ({
        url: "comments/public/rate",
        params: {
          item_type: params.item_type,
          item_id: params.item_id,
        },
      }),
      providesTags: (result) => [{ type: "Rate", id: "RATE" }],
    }),

    //post Comment

    postCommentPublic: builder.mutation<
      Pick<CommentJson, "id">,
      CommentJsonAddEdit
    >({
      query(body) {
        delete body.rate;
        if (body.images.length < 1) {
          delete body.images;
        }
        return {
          url: "comments/public/comment",
          method: "post",
          body: {
            ...body,
          },
        };
      },
      invalidatesTags: (result, error, body) => [
        // { type: "CommentPublic", id: "COMMENT_LIST_PUBLIC" },
        // { type: "ReviewPublic", id: "REVIEW_lIST_PUBLIC" },
      ],
    }),

    //post QA public
    postQAPublic: builder.mutation<
      Pick<CommentJson, "id">,
      Pick<CommentJsonAddEdit, "detail">
    >({
      query(body) {
        return {
          url: "comments/public/qa",
          method: "post",
          body: { ...body },
        };
      },
      invalidatesTags: (result, error, body) => [
        { type: "QaPublic", id: "QA_LIST_PUBLIC" },
      ],
    }),

    //post review public
    postReviewPublic: builder.mutation<
      Pick<CommentJson, "id">,
      Pick<CommentJsonAddEdit, "detail" | "rate" | "images" | "phone">
    >({
      query(body) {
        return {
          url: "comments/public/review",
          method: "post",
          body,
        };
      },
      invalidatesTags: (result, error, body) => [
        { type: "ReviewPublic", id: "REVIEW_LIST_PUBLIC" },
        { type: "Rate", id: "RATE" },
        { type: "ImagePublic", id: "IMAGE_LIST_PUBLIC" },
      ],
    }),

    ///////////////////////

    // api login

    getCommentListLogin: builder.query<CommentCollectionJson, FilterComment>({
      query: (params: FilterComment & Jwt) => ({
        url: "comments/comment",
        params: {
          customer_id:
            params.customer_id && params.customer_id > 0
              ? params.customer_id
              : undefined,
          item_id:
            params.item_id && params.item_id !== ""
              ? params.item_id
              : undefined,
          item_type:
            params.item_type && params.item_type !== ""
              ? params.item_type
              : undefined,
          parent_id:
            params.parent_id && params.parent_id > 0
              ? params.parent_id
              : undefined,
          ...params,
        },
        method: "get",
        timeout: 7000,
      }),

      transformErrorResponse(response, meta, arg) {
        return response;
      },
      transformResponse(response: CommentCollectionJson, _) {
        return response;
      },
      keepUnusedDataFor: 10,

      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "CommentLogin" as const,
                id,
              })),
              { type: "CommentLogin", id: "COMMENT_LIST_LOGIN" },
            ]
          : [{ type: "CommentLogin", id: "COMMENT_LIST_LOGIN" }],
    }),

    //

    getQAListLogin: builder.query<CommentCollectionJson, FilterComment>({
      query: (params: FilterComment) => ({
        url: "comments/qa",
        params: {
          item_id: params.item_id,
          item_type: params.item_type,
          parent_id:
            params.parent_id && params.parent_id > 0
              ? params.parent_id
              : undefined,
          ...params,
        },
        method: "get",
        timeout: 7000,
      }),
      keepUnusedDataFor: 0,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "QaLogin" as const,
                id,
              })),
              { type: "QaLogin", id: "QA_LIST_LOGIN" },
            ]
          : [{ type: "QaLogin", id: "QA_LIST_LOGIN" }],
    }),

    //

    getReviewListLogin: builder.query<CommentCollectionJson, FilterComment>({
      query: (params: FilterComment) => ({
        url: "comments/review",
        params: {
          ...params,
          item_id:
            params.item_id && params.item_id !== ""
              ? params.item_id
              : undefined,
          item_type:
            params.item_type && params.item_type !== ""
              ? params.item_type
              : undefined,
          parent_id:
            params.parent_id && params.parent_id > 0
              ? params.parent_id
              : undefined,
        },
        method: "get",
        timeout: 7000,
      }),
      keepUnusedDataFor: 0,

      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "ReviewLogin" as const,
                id,
              })),
              { type: "ReviewLogin", id: "REVIEW_LIST_LOGIN" },
            ]
          : [{ type: "ReviewLogin", id: "REVIEW_LIST_LOGIN" }],
    }),

    //

    getLikedByUser: builder.query<
      any,
      Pick<CommentJsonAddEdit, "item_type" | "item_id">
    >({
      query: (params) => {
        return {
          url: "comments/history",
          method: "get",

          params: {
            ...params,
            item_id: params.item_id,
            item_type: params.item_type,
          },
        };
      },
      providesTags: (result, err) =>
        result
          ? [
              ...result.map((i: number, index: any) => ({
                type: "LikedLogin" as const,
                i,
              })),
              { type: "LikedLogin", id: "LIKED_LOGIN" },
            ]
          : [{ type: "LikedLogin", id: "LIKED_LOGIN" }],
      keepUnusedDataFor: 60,
    }),

    //edit Comment

    editComment: builder.mutation<CommentJson, CommentJsonAddEdit>({
      query: (body) => ({
        url: `comments/comment/${body.id}`,
        method: "put",
        body: {
          ...body,
        },
        params: {},
      }),
      invalidatesTags: (result, error, body) => [
        { type: "CommentLogin", id: "COMMENT_LIST_LOGIN" },
      ],
    }),

    //edit Review

    editReview: builder.mutation<CommentJson, CommentJsonAddEdit>({
      query: (body) => ({
        url: `comments/review/${body.id}`,
        method: "put",
        body: {
          ...body,
        },
        params: {},
      }),
      invalidatesTags: (result, error, body) => [
        { type: "ReviewLogin", id: "REVIEW_LIST_LOGIN" },
      ],
    }),

    editQa: builder.mutation<any, CommentJsonAddEdit>({
      query: (body) => ({
        url: `comments/qa/${body.id}`,
        method: "put",
        body: { ...body },
        params: {},
      }),
      invalidatesTags: (result, error, body) => {
        return result && !error
          ? [
              {
                type: "QaLogin",
                id: "QA_LIST_LOGIN",
              },
            ]
          : [];
      },
    }),

    // & Required<Pick<CommentJsonAddEdit, "customer_id">>
    postCommentLogin: builder.mutation<
      Pick<CommentJson, "id">,
      CommentJsonAddEdit
    >({
      query(body) {
        delete body.rate;
        if (body.images.length < 1) {
          delete body.images;
        }
        return {
          url: "comments/comment",
          method: "post",
          body: {
            ...body,
          },

          params: {},
        };
      },
      invalidatesTags: (result, error, body) => [
        { type: "CommentLogin", id: "COMMENT_LIST_LOGIN" },
        // { type: "ReviewLogin", id: "REVIEW_LIST_LOGIN" },
      ],
    }),

    //

    postQALogin: builder.mutation<Pick<CommentJson, "id">, CommentJsonAddEdit>({
      query(body) {
        return {
          url: "comments/qa",
          method: "post",
          body,
          params: {},
        };
      },
      invalidatesTags: (result, error, body) => [
        { type: "QaLogin", id: "QA_LIST_LOGIN" },
      ],
    }),

    //

    postReviewLogin: builder.mutation<
      Pick<CommentJson, "id">,
      CommentJsonAddEdit
    >({
      query(body) {
        return {
          url: "comments/review",
          method: "post",
          body: {
            ...body,
          },
          params: {},
        };
      },
      invalidatesTags: (result, error, body) => {
        if (result) {
          return [
            { type: "ReviewLogin", id: "REVIEW_LIST_LOGIN" },
            { type: "Rate", id: "RATE" },
            { type: "ImagePublic", id: "IMAGE_LIST_PUBLIC" },
          ];
        } else {
          return [];
        }
      },
    }),

    postLikeLogin: builder.mutation<any, Pick<CommentJson, "like" | "id">>({
      query(body) {
        return {
          url: `comments/like/${body.id}`,
          method: "put",
          body: {
            like: body.like,
          },
          params: {},
        };
      },
      invalidatesTags: (result, error, body) => {
        if (result) {
          return [
            // { type: "CommentLogin", id: "COMMENT_LIST_LOGIN" },
            // { type: "ReviewLogin" as const, id: "REVIEW_LIST_LOGIN" },
            { type: "LikedLogin" as const, id: "LIKED_LOGIN" },
            // { type: "QaLogin", id: "QA_LIST_LOGIN" },
          ];
        } else {
          return [];
        }
      },
    }),

    //delete Comment
    deleteComment: builder.mutation<boolean, Pick<CommentJson, "id">>({
      query: (body) => ({
        url: `comments/delete/${body.id}`,
        method: "put",
        params: {
          __jwtAuthorization: localStorage.getItem("MAIN:token"),
        },
      }),
      invalidatesTags: (result, error) => {
        if (result) {
          return [
            { type: "CommentLogin", id: "COMMENT_LIST_LOGIN" },
            { type: "ReviewLogin", id: "REVIEW_LIST_LOGIN" },
            { type: "QaLogin", id: "QA_LIST_LOGIN" },
          ];
        } else {
          return [];
        }
      },
    }),

    ///////////////////////

    //Api Admin
    //deleteAmdin
    deleteAdmin: builder.mutation<CommentJson, Pick<CommentJson, "id">>({
      query: (body) => ({
        url: `comments/admin/delete/${body.id}`,
        method: "put",
        body,
      }),
    }),
    //

    editAdmin: builder.mutation<
      CommentJson,
      Partial<CommentJsonAddEdit> & Pick<CommentJson, "id">
    >({
      query: (body) => ({
        url: `comments/admin/editadmin/${body.id}`,
        method: "put",
        body,
      }),
    }),
  }),

  // middleware: (getDefaultMiddleware) => {
  //   return getDefaultMiddleware().concat(api.middleware).concat((getDefaultMiddleware) =>
  //     getDefaultMiddleware({
  //       serializableCheck: false,
  //     }).concat((builder) => ({
  //       async baseQuery(endpoint, arg, { dispatch, getState }) {
  //         const { url, ...rest } = endpoint;
  //         const token = getState().auth.token; // Thay thế 'auth' và 'token' với phần tương ứng trong Redux store của bạn
  //         const modifiedUrl = `${url}?token=${token}`; // Thay thế 'token' với tên param tương ứng trong yêu cầu của bạn
  //         return fetchBaseQuery({ baseUrl: '/api' }).baseQuery({ ...rest, url: modifiedUrl }, arg, { dispatch, getState });
  //       },
  //       Ư

  // middleware: (getDefaultMiddleware) =>
  //   getDefaultMiddleware().concat((api) => (next) => (action) => {
  //     if (action.type === "auth/setToken") {
  //       const decodedToken: { exp: number } = jwt_decode(action.payload);
  //       const expirationTime = decodedToken.exp * 1000 - Date.now();
  //       setTimeout(() => {
  //         api.dispatch(clearToken());
  //       }, expirationTime);
  //     }
  //     return next(action);
  //   }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  //public
  useGetCommentListPublicQuery,
  useGetCommentListTestQuery,
  useGetImageListPublicQuery,
  useGetReviewListPublicQuery,
  useGetRatePublicQuery,

  usePostCommentPublicMutation,
  usePostQAPublicMutation,
  usePostReviewPublicMutation,

  // login
  useGetCommentListLoginQuery,
  useGetReviewListLoginQuery,
  useGetQAListLoginQuery,
  useGetLikedByUserQuery,

  useEditCommentMutation,
  useEditReviewMutation,
  useDeleteCommentMutation,
  useEditQaMutation,

  usePostCommentLoginMutation,
  usePostQALoginMutation,
  usePostReviewLoginMutation,
  usePostLikeLoginMutation,

  //admin
  useGetQAListPublicQuery,

  useDeleteAdminMutation,
  useEditAdminMutation,
  //
} = commentApi;
